import { useEffect } from "react";
import { MapPin, Clock, ArrowUpRight, Flame, Quote, Star } from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import BBQMenu from "@/components/BBQMenu";
import BBQDishes from "@/components/BBQDishes";
import Marquee from "@/components/Marquee";
import { Reveal } from "@/lib/useReveal";
import { BBQ, IMAGES, waLink } from "@/lib/brand";
import { useSEO } from "@/lib/useSEO";

import { useLang } from "@/i18n/LangContext";

export default function BBQPage() {
  const { t } = useLang();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: "BBQ Assador — Casa de Petiscos | Costa da Caparica",
    description:
      "BBQ Assador, Casa de Petiscos na Costa da Caparica. Grelha portuguesa, peixe fresco, carnes selecionadas e petiscos tradicionais. Venha com fome. Reserve já.",
    image: IMAGES.bbqExterior,
    imageAlt: "BBQ Assador — Casa de Petiscos · Costa da Caparica",
    type: "restaurant",
    themeColor: "#1A0E08",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Restaurant",
      name: "BBQ Assador — Casa de Petiscos",
      description:
        "Casa de Petiscos na Costa da Caparica. Grelha portuguesa, peixe fresco, carnes selecionadas e petiscos tradicionais.",
      servesCuisine: ["Portuguesa", "Grelhados", "Petiscos", "Carnes"],
      priceRange: "€€",
      telephone: "+351917240397",
      image: [IMAGES.bbqExterior, IMAGES.bbqInterior],
      url: typeof window !== "undefined" ? window.location.href : "",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Av. Gen. Humberto Delgado 31 D",
        postalCode: "2825-343",
        addressLocality: "Costa da Caparica",
        addressCountry: "PT",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 38.6438,
        longitude: -9.2331,
      },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ],
          opens: "10:00",
          closes: "22:00",
        },
      ],
      hasMenu: "/menu-bbq.pdf",
    },
  });

  const message = t("wa.bbq.reserve");

  return (
    <main className="theme-bbq relative min-h-screen bg-[#1A0E08] text-[#F5EDD9]" data-testid="bbq-page">
      <BrandHeader theme="bbq" logoSrc={BBQ.logo} name="BBQ Assador" />

      {/* ============ HERO — real exterior photo ============ */}
      <section className="relative min-h-[100svh] w-full overflow-hidden bg-[#1A0E08]">
        {/* Real photo — crop on the wooden "BBQ · Casa de Petiscos" sign at the top */}
        <img
          src={IMAGES.bbqExterior}
          alt="BBQ Assador — Casa de Petiscos · Costa da Caparica"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover anim-slow-zoom img-enhance"
          style={{ objectPosition: "50% 6%" }}
        />
        {/* Warm cinematic gradient — readable on left, ember tones */}
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(15,8,3,0.82)] via-[rgba(15,8,3,0.45)] to-[rgba(15,8,3,0.25)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A0E08] via-[#1A0E08]/35 via-55% to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_18%,rgba(232,90,31,0.18),transparent_55%)]" />

        {/* Vertical label */}
        <div className="hidden md:flex absolute left-8 top-1/2 -translate-y-1/2 items-center pointer-events-none">
          <span className="vertical-label text-[#D4A849]/80 font-sans-bbq">
            Fogo · Sabor · Petiscos
          </span>
        </div>

        {/* Chapter numeral */}
        <div className="hidden md:block absolute top-28 right-10 lg:right-16 z-10 pointer-events-none text-right">
          <span className="text-[10px] tracking-[0.5em] uppercase text-[#E85A1F] font-sans-bbq">
            Capítulo
          </span>
          <div className="mt-1 font-serif-bbq text-[#E85A1F] text-6xl">II</div>
        </div>

        <div className="relative z-10 max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-12 pt-32 sm:pt-36 pb-14 sm:pb-20 min-h-[100svh] flex flex-col justify-end">
          <Reveal variant="up" className="max-w-4xl">
            <div className="flex items-center gap-4 text-[#E85A1F] text-[10px] tracking-[0.45em] uppercase font-sans-bbq">
              <span className="h-px w-10 bg-[#E85A1F]/70 reveal-line is-visible" />
              Casa de Petiscos · Costa da Caparica
            </div>
            <h1 className="mt-5 font-serif-bbq text-[#F5EDD9] text-[clamp(3.5rem,12vw,10rem)] leading-[0.85] font-bold tracking-[-0.04em]">
              BBQ
              <br />
              <em className="italic text-[#E85A1F]">Assador.</em>
            </h1>
            <p className="mt-8 text-[#F5EDD9]/85 text-base sm:text-lg lg:text-xl font-sans-bbq max-w-xl leading-relaxed">
              Fogo, sabor e petiscos com identidade.
            </p>
            <p className="mt-4 text-[#9A8F7D] text-sm sm:text-base font-sans-bbq max-w-xl leading-relaxed">
              Um espaço pensado para quem gosta de comer bem, partilhar à mesa e voltar.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-5 sm:items-center">
              <a
                href={waLink(message)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-reserve-bbq"
                className="btn btn-lg btn-primary-bbq w-full sm:w-auto justify-center"
              >
                Reservar via WhatsApp
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
              </a>
              <a
                href="#menu"
                data-testid="hero-view-menu-bbq"
                className="link-underline inline-flex items-center justify-center sm:justify-start gap-2 text-[#F5EDD9]/85 hover:text-[#F5EDD9] text-[11px] tracking-[0.32em] uppercase font-sans-bbq transition-colors duration-300 py-2"
              >
                Ver Menu
                <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
              </a>
            </div>
          </Reveal>

          <div className="mt-14 hidden sm:flex items-end justify-between text-[#9A8F7D] text-[10px] tracking-[0.4em] uppercase font-sans-bbq">
            <span>Grelha · Peixe · Carnes</span>
            <div className="flex items-center gap-3">
              <span>Takeaway</span>
              <span className="scroll-hint text-[#E85A1F]/60" aria-hidden />
            </div>
            <span>Todos os dias · 10:00 — 22:00</span>
          </div>
        </div>
      </section>

      {/* ============ MARQUEE — orange contrast band ============ */}
      <div className="relative bg-[#E85A1F] overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-px bg-[#1A0E08]/25" />
        <Marquee
          items={[
            "Do lume ao prato",
            "Peixe fresco",
            "Carnes selecionadas",
            "Petiscos à portuguesa",
            "Takeaway disponível",
            "Grelha portuguesa",
          ]}
          separator="•"
          textClass="font-serif-bbq italic text-[#1A0E08] text-5xl sm:text-6xl lg:text-7xl tracking-tight"
        />
        <div className="absolute bottom-0 inset-x-0 h-px bg-[#1A0E08]/25" />
      </div>

      {/* ============ GRELHA — texto editorial, sem foto ============ */}
      <section id="grelha" className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 sm:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <Reveal variant="up">
              <span className="text-[10px] tracking-[0.5em] uppercase text-[#E85A1F] font-sans-bbq">
                Capítulo 01 — O sabor está na grelha
              </span>
              <h2 className="mt-6 font-serif-bbq text-[#F5EDD9] text-5xl sm:text-6xl lg:text-7xl leading-[1] tracking-[-0.035em]">
                Fogo, tempo
                <br />
                e <em className="italic text-[#E85A1F]">tradição</em>
                <br />
                portuguesa.
              </h2>
              <div className="mt-8 h-px w-16 bg-[#E85A1F] reveal-line is-visible" />
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal variant="up" delay={150}>
              <div className="space-y-6 text-[#9A8F7D] font-sans-bbq text-base sm:text-lg leading-relaxed max-w-xl">
                <p>
                  No coração da Costa da Caparica, o{" "}
                  <span className="text-[#F5EDD9]">BBQ Assador — Casa de Petiscos</span>{" "}
                  oferece uma experiência focada no essencial: boa comida,
                  ambiente confortável e sabores tradicionais preparados com
                  consistência.
                </p>
                <p>
                  Do peixe grelhado às carnes e petiscos tradicionais, cada prato é
                  pensado para quem gosta de comer bem, sem excessos, mas com
                  identidade.
                </p>
              </div>
            </Reveal>

            {/* 4 pilares — texto, sem imagens */}
            <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
              {[
                { n: "i.", k: "Grelha", v: "Portuguesa, lenta, no ponto certo. Tradição que dá carácter a cada prato." },
                { n: "ii.", k: "Peixe", v: "Fresco e tradicional. Do mar à grelha, no mesmo dia." },
                { n: "iii.", k: "Carnes", v: "Selecionadas, no timing certo. Picanha, entrecosto e borrego com temperamento." },
                { n: "iv.", k: "Takeaway", v: "Para levar o sabor para casa. Embalado com o mesmo cuidado." },
              ].map(({ n, k, v }, i) => (
                <Reveal key={k} variant="up" delay={100 + i * 100} className="group">
                  <div className="flex items-start justify-between pb-4 border-b border-[#F5EDD9]/15 group-hover:border-[#E85A1F]/70 transition-colors duration-700">
                    <span className="font-serif-bbq italic text-4xl text-[#E85A1F] leading-none">
                      {n}
                    </span>
                    <Flame className="h-5 w-5 text-[#D4A849] mt-2" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 font-serif-bbq text-xl sm:text-2xl text-[#F5EDD9] tracking-tight">
                    {k}
                  </h3>
                  <p className="mt-2 font-sans-bbq text-sm text-[#9A8F7D] leading-[1.8]">
                    {v}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ EXPERIÊNCIA — text-driven, no photos ============ */}
      <section
        id="espaco"
        data-testid="bbq-experiencia"
        className="relative py-24 sm:py-32 bg-[#1A0E08] overflow-hidden border-y border-[#E85A1F]/12"
      >
        {/* Layered atmospheric texture — ember warmth */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A0E08] via-[#221309] to-[#1A0E08]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_50%,rgba(232,82,26,0.10),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_30%,rgba(232,90,31,0.16),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_85%,rgba(212,168,73,0.10),transparent_60%)]" />
        <div className="absolute inset-0 grain-soft" />

        {/* Subtle vertical accent line */}
        <div className="hidden lg:block absolute left-12 top-1/2 -translate-y-1/2 h-32 w-px bg-gradient-to-b from-transparent via-[#E85A1F]/40 to-transparent" />

        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          {/* HEADLINE BLOCK */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-end">
            <Reveal variant="up" className="lg:col-span-7">
              <span className="text-[10px] tracking-[0.5em] uppercase text-[#E85A1F] font-sans-bbq">
                Capítulo 02 — Experiência
              </span>
              <h2 className="mt-7 font-serif-bbq text-[#F5EDD9] text-[clamp(3rem,7.5vw,6.5rem)] leading-[0.92] tracking-[-0.045em]">
                Mais do que comer.
                <br />
                <em className="italic text-[#E85A1F]">É sentir o fogo.</em>
              </h2>
              <p className="mt-10 font-serif-bbq italic text-[#D4A849] text-2xl sm:text-3xl leading-[1.25] tracking-[-0.01em] max-w-xl">
                Carne no ponto certo. Petiscos sem pressa.
              </p>
            </Reveal>

            <Reveal variant="up" delay={150} className="lg:col-span-5 lg:pb-2">
              <div className="h-px w-12 bg-[#E85A1F] mb-8" />
              <p className="font-sans-bbq text-[#F5EDD9]/80 text-base sm:text-lg leading-[1.7] max-w-md">
                Um espaço pensado para quem valoriza{" "}
                <span className="text-[#F5EDD9]">sabor a sério</span>.
                Aqui não há pressa — há grelha, há partilha e há ambiente.
              </p>

              {/* 3 value props */}
              <ul className="mt-9 space-y-3.5 max-w-md">
                {[
                  "Cozinha aberta todos os dias",
                  "Grelha portuguesa, sem atalhos",
                  "Ambiente simples, sabor a sério",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 font-sans-bbq text-[#F5EDD9]/90 text-[15px] leading-relaxed"
                  >
                    <Flame
                      className="h-4 w-4 mt-1 text-[#E85A1F] shrink-0"
                      strokeWidth={1.8}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              {/* Tagline */}
              <div className="mt-9 pt-5 border-t border-[#E85A1F]/25">
                <span className="font-serif-bbq italic text-[#D4A849] text-base sm:text-lg">
                  Há anos na Costa da Caparica.
                </span>
              </div>
            </Reveal>
          </div>

          {/* THREE PILLARS */}
          <div className="mt-24 sm:mt-32 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                num: "01",
                glyph: "Grelha",
                copy: "Carne no ponto, feita com consistência.",
                tagline: "Feita com lenha e paciência.",
                icon: GrillGlyph,
                bg: "/dishes/lagareiro.jpg",
                bgPos: "center 35%",
              },
              {
                num: "02",
                glyph: "Petiscos",
                copy: "Para partilhar, sem complicações.",
                tagline: "Porções generosas. Mesa que une.",
                icon: PlateGlyph,
                bg: "https://customer-assets.emergentagent.com/job_two-tables-one-coast/artifacts/eeb216i7_IMG_1077.jpeg",
                bgPos: "center",
              },
              {
                num: "03",
                glyph: "Ambiente",
                copy: "Simples, acolhedor e real.",
                tagline: "Entra como cliente. Sai como habitué.",
                icon: HearthGlyph,
                bg: IMAGES.bbqInterior,
                bgPos: "70% 35%",
              },
            ].map(({ num, glyph, copy, tagline, icon: Glyph, bg, bgPos }, i) => (
              <Reveal
                key={glyph}
                variant="up"
                delay={i * 140}
                data-testid={`bbq-experiencia-${glyph.toLowerCase()}`}
                className="group relative overflow-hidden rounded-sm aspect-[4/5] sm:aspect-[3/4] md:aspect-[4/5]"
              >
                {/* Background image */}
                <div
                  className="absolute inset-0 bg-cover transition-transform duration-[1800ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06] img-enhance"
                  style={{ backgroundImage: `url(${bg})`, backgroundPosition: bgPos }}
                />
                {/* Warm dark overlay — embraces photo, lifts on hover */}
                <div className="absolute inset-0 bg-[rgba(26,14,8,0.72)] group-hover:bg-[rgba(26,14,8,0.55)] transition-colors duration-700" />
                {/* Bottom orange glow */}
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#1A0E08] via-[#221309]/70 to-transparent" />
                {/* Hover orange wash */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-[radial-gradient(circle_at_50%_100%,rgba(232,90,31,0.18),transparent_65%)]" />

                {/* Content */}
                <div className="relative h-full flex flex-col justify-between p-8 sm:p-10 lg:p-12">
                  <div className="flex items-start justify-between">
                    <span className="font-serif-bbq italic text-[#E85A1F] text-5xl leading-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                      {num}
                    </span>
                    <Glyph className="h-8 w-8 text-[#D4A849] drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)] transition-transform duration-700 group-hover:rotate-3 group-hover:scale-105" />
                  </div>

                  <div>
                    <h3 className="font-serif-bbq text-[#F5EDD9] text-3xl sm:text-4xl tracking-[-0.02em] leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
                      {glyph}
                    </h3>
                    <p className="mt-3 font-sans-bbq text-[#F5EDD9]/85 text-sm sm:text-base leading-relaxed max-w-xs drop-shadow">
                      {copy}
                    </p>
                    <p className="mt-3 font-serif-bbq italic text-[#D4A849] text-sm sm:text-base">
                      {tagline}
                    </p>
                    <div className="mt-5 h-px w-10 bg-[#E85A1F]/60 transition-all duration-700 group-hover:w-24 group-hover:bg-[#E85A1F]" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* CTA STRIP — WhatsApp primary, Ver Menu as text link */}
          <Reveal
            variant="up"
            delay={300}
            className="mt-20 sm:mt-24 flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-8"
          >
            <a
              href={waLink(message)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bbq-experiencia-reserve"
              className="btn btn-lg btn-primary-bbq w-full sm:w-auto justify-center"
            >
              Reservar via WhatsApp
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
            </a>
            <a
              href="#menu"
              data-testid="bbq-experiencia-menu"
              className="link-underline inline-flex items-center gap-2 text-[#F5EDD9]/85 hover:text-[#F5EDD9] text-[11px] tracking-[0.32em] uppercase font-sans-bbq transition-colors duration-300"
            >
              Ver Menu
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* ============ TESTEMUNHO — sem foto, tipografia editorial ============ */}
      <section className="relative py-24 sm:py-32 bg-[#1A0E08] overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,168,73,0.08),transparent_65%)]" />
        <div className="relative max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <Reveal variant="up">
            <Quote className="h-10 w-10 text-[#E85A1F] mx-auto" strokeWidth={1} />
            <p className="mt-10 font-serif-bbq italic text-[#F5EDD9] text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.2] tracking-[-0.015em]">
              “Grelhados tradicionais feitos com cuidado — ambiente confortável
              e porções <em className="not-italic">generosas</em>.”
            </p>
            <div className="mt-10 flex items-center justify-center gap-6">
              <span className="h-px flex-1 max-w-20 bg-[#F5EDD9]/15" />
              <div className="inline-flex items-center gap-1 text-[#E85A1F]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-[#E85A1F]" strokeWidth={0} />
                ))}
              </div>
              <span className="h-px flex-1 max-w-20 bg-[#F5EDD9]/15" />
            </div>
            <div className="mt-6 text-[10px] tracking-[0.4em] uppercase text-[#9A8F7D] font-sans-bbq">
              Opinião Google · Cozinha portuguesa
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ MOMENTO EDITORIAL — full-width dish photo ============ */}
      <section
        id="momento"
        data-testid="bbq-momento"
        className="relative w-full overflow-hidden bg-[#1A0E08]"
        aria-label="Do mar para a brasa"
      >
        <div className="relative h-[70svh] min-h-[480px] max-h-[760px] w-full overflow-hidden">
          <img
            src="/dishes/lagareiro.jpg"
            alt="Bacalhau à Lagareiro — assado com azeite e alho"
            className="absolute inset-0 w-full h-full object-cover img-enhance scale-105"
            loading="lazy"
          />
          {/* Cinematic gradients — preserve photo readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A0E08]/85 via-[#1A0E08]/30 to-[#1A0E08]/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A0E08]/70 via-transparent to-[#1A0E08]/30" />

          {/* Editorial copy */}
          <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 h-full flex items-center">
            <Reveal variant="up" className="max-w-xl">
              <span className="text-[10px] tracking-[0.5em] uppercase text-[#E85A1F] font-sans-bbq">
                Capítulo 03 — Editorial
              </span>
              <h2 className="mt-5 font-serif-bbq text-[#F5EDD9] text-5xl sm:text-6xl lg:text-7xl tracking-[-0.035em] leading-[0.95]">
                Do mar
                <br />
                <em className="italic text-[#E85A1F]">para a brasa.</em>
              </h2>
              <p className="mt-7 font-sans-bbq text-[#F5EDD9]/85 text-base sm:text-lg leading-relaxed max-w-md">
                Azeite, alho, tradição. Pratos que vieram da costa e ficaram no
                ponto certo da grelha.
              </p>
              <div className="mt-8 inline-flex items-center gap-3 text-[10px] tracking-[0.45em] uppercase text-[#D4A849] font-sans-bbq">
                <span className="h-px w-8 bg-[#D4A849]" />
                Bacalhau à Lagareiro
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ PRATOS EM DESTAQUE — fotos reais dos pratos ============ */}
      <BBQDishes />

      {/* ============ MENU — light sand contrast ============ */}
      <section id="menu" className="relative">
        <BBQMenu />
      </section>

      {/* ============ VISITAR ============ */}
      <section id="visitar" className="relative bg-[#1A0E08]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-12">
            <Reveal variant="up">
              <span className="text-[10px] tracking-[0.5em] uppercase text-[#E85A1F] font-sans-bbq">
                Capítulo 05 — Visitar
              </span>
              <h2 className="mt-5 font-serif-bbq text-[#F5EDD9] text-5xl sm:text-6xl tracking-[-0.035em] leading-[1]">
                Venha com
                <br />
                <em className="italic text-[#E85A1F]">fome.</em>
              </h2>
            </Reveal>

            <InfoRowDark icon={MapPin} title="Morada">
              <div className="text-[#F5EDD9]">{BBQ.address}</div>
              <div className="mt-3">
                <a
                  href={BBQ.mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="bbq-maps-link"
                  className="link-underline inline-flex items-center gap-1 text-[#E85A1F] text-[11px] tracking-[0.32em] uppercase"
                >
                  Abrir no Google Maps <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </InfoRowDark>

            <InfoRowDark icon={Clock} title="Horário">
              <div className="flex justify-between gap-4 border-b border-[#F5EDD9]/15 py-2.5">
                <span className="text-[#9A8F7D]">Todos os dias</span>
                <span className="text-[#F5EDD9]">10h00 — 22h00</span>
              </div>
            </InfoRowDark>

            <InfoRowDark icon={ArrowUpRight} title="Reservas">
              <a
                href={waLink(message)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bbq-reserve-contact"
                className="link-underline inline-flex items-center gap-2 text-[#F5EDD9]"
              >
                WhatsApp · 917 240 397
              </a>
            </InfoRowDark>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2">
              <a
                href={waLink(message)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bbq-visitar-reserve"
                className="btn btn-md btn-primary-bbq w-full sm:w-auto"
              >
                Reservar Mesa
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
              </a>
              <a
                href={BBQ.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bbq-how-to-arrive"
                className="btn btn-md btn-ghost-bbq w-full sm:w-auto"
              >
                Como Chegar
                <MapPin className="h-4 w-4" strokeWidth={1.75} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal variant="in" className="relative w-full aspect-[4/3] lg:aspect-[16/13] overflow-hidden border border-[#E85A1F]/25">
              <iframe
                title="Mapa BBQ Assador"
                src={BBQ.mapsQuery}
                className="absolute inset-0 w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ PROVA SOCIAL ============ */}
      <section
        id="reviews"
        data-testid="bbq-reviews"
        className="relative py-24 sm:py-32 bg-[#1A0E08] overflow-hidden border-t border-[#E85A1F]/15"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(232,90,31,0.16),transparent_55%)]" />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal variant="up" className="max-w-2xl mb-14 sm:mb-20">
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#E85A1F] font-sans-bbq">
              Prova social
            </span>
            <h2 className="mt-5 font-serif-bbq text-[#F5EDD9] text-4xl sm:text-5xl lg:text-6xl tracking-[-0.025em] leading-[1]">
              O que dizem
              <br />
              <em className="italic text-[#E85A1F]">os nossos clientes.</em>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                text: "A picanha estava no ponto certo. Ambiente descontraído, serviço rápido. Voltámos na semana seguinte.",
                name: "Tiago M.",
                meta: "Costa da Caparica",
              },
              {
                text: "Os petiscos são para partilhar mas fica difícil não comer tudo sozinho. O melhor assador da zona.",
                name: "Ana Rita F.",
                meta: "Almada",
              },
              {
                text: "Ambiente acolhedor, preços justos e a carne grelhada como deve ser. Recomendo sem hesitar.",
                name: "Ricardo V.",
                meta: "Setúbal",
              },
            ].map((r, i) => (
              <Reveal
                key={r.name}
                variant="up"
                delay={i * 140}
                data-testid={`bbq-review-${i}`}
                className="relative rounded-2xl bg-[#221309]/90 border border-[#E85A1F]/25 p-7 sm:p-9 transition-all duration-500 hover:border-[#E85A1F]/70 hover:bg-[#2A1810]"
              >
                <Quote className="h-7 w-7 text-[#E85A1F] mb-5" strokeWidth={1} />
                <div className="flex items-center gap-1 mb-5 text-[#E85A1F]">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-[#E85A1F]" strokeWidth={0} />
                  ))}
                </div>
                <p className="font-sans-bbq text-[#F5EDD9]/90 text-[15px] leading-[1.8]">
                  “{r.text}”
                </p>
                <div className="mt-7 pt-5 border-t border-[#F5EDD9]/10">
                  <div className="font-serif-bbq italic text-[#F5EDD9] text-lg">
                    {r.name}
                  </div>
                  <div className="mt-1 text-[10px] tracking-[0.3em] uppercase text-[#9A8F7D] font-sans-bbq">
                    {r.meta}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA FINAL — warm ember gradient ============ */}
      <section className="relative py-28 sm:py-36 bg-[#1A0E08] border-t border-[#E85A1F]/15 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#221309] via-[#1A0E08] to-[#0F0703]" />
        <div className="absolute inset-0 bg-[rgba(15,8,3,0.45)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(232,82,26,0.22),rgba(15,8,3,0.0)_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_90%,rgba(212,168,73,0.14),transparent_55%)]" />
        <div className="absolute inset-0 grain-soft opacity-60" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal variant="up">
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#E85A1F] font-sans-bbq">
              A sua mesa
            </span>
            <h2 className="mt-6 font-serif-bbq text-[#F5EDD9] text-5xl sm:text-7xl lg:text-8xl tracking-[-0.04em] leading-[1]">
              Reserve.
              <br />
              <em className="italic text-[#E85A1F]">Depois é só sentar-se.</em>
            </h2>
            <a
              href={waLink(message)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bbq-final-cta"
              className="btn btn-lg btn-primary-bbq btn-pulse-bbq mt-14"
            >
              Reservar via WhatsApp
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
            </a>
            <p className="mt-6 text-[11px] tracking-[0.32em] uppercase text-[#9A8F7D] font-sans-bbq">
              Resposta em menos de 5 minutos · Todos os dias
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer
        data-testid="bbq-footer"
        className="bg-[#0F0703] text-[#F5EDD9]/80 pt-14 pb-24 sm:pb-10 relative"
      >
        {/* Top orange line */}
        <div className="absolute top-0 inset-x-0 h-[3px] bg-[#E85A1F]" />

        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
          {/* Brand wordmark — typographic, no image */}
          <div className="flex flex-col items-center md:items-start gap-3 text-center md:text-left">
            <span className="inline-flex flex-col leading-none gap-1.5">
              <span className="font-serif-bbq text-[#F5EDD9] text-3xl sm:text-4xl tracking-tight leading-none">
                BBQ
              </span>
              <span className="font-sans-bbq text-[#E85A1F] text-[10px] tracking-[0.45em] uppercase leading-none">
                Assador
              </span>
            </span>
            <span className="font-serif-bbq italic text-[#F5EDD9]/95 text-lg sm:text-xl mt-1">
              Casa de Petiscos
            </span>
            <span className="text-[10px] tracking-[0.4em] uppercase text-[#F5EDD9]/55 font-sans-bbq">
              Costa da Caparica
            </span>
          </div>

          {/* Nav */}
          <nav
            aria-label="Navegação rodapé"
            className="flex flex-col items-center gap-3 text-[11px] tracking-[0.3em] uppercase font-sans-bbq"
          >
            {[
              { href: "#grelha", label: "Grelha" },
              { href: "#espaco", label: "Experiência" },
              { href: "#pratos-destaque", label: "Pratos" },
              { href: "#menu", label: "Menu" },
              { href: "#visitar", label: "Visitar" },
              { href: "/tarquinio", label: "Tarquínio" },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="link-underline text-[#F5EDD9]/75 hover:text-[#E85A1F] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          {/* Social + meta */}
          <div className="flex flex-col items-center md:items-end gap-4">
            <div className="flex items-center gap-5">
              <a
                href="https://instagram.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="bbq-social-instagram"
                className="text-[#F5EDD9]/70 hover:text-[#E85A1F] transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
                  <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.5" />
                  <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5" />
                  <circle cx="17.5" cy="6.5" r="0.9" fill="currentColor" />
                </svg>
              </a>
              <a
                href="https://facebook.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="bbq-social-facebook"
                className="text-[#F5EDD9]/70 hover:text-[#E85A1F] transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden>
                  <path d="M13.5 21v-7.5h2.5l.4-3h-2.9V8.65c0-.86.24-1.45 1.5-1.45H17V4.6c-.27-.04-1.16-.1-2.2-.1-2.18 0-3.67 1.32-3.67 3.74V10.5H8.5v3h2.63V21h2.37z" />
                </svg>
              </a>
            </div>
            <a
              href={waLink(message)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] tracking-[0.32em] uppercase text-[#F5EDD9]/70 hover:text-[#E85A1F] transition-colors font-sans-bbq"
            >
              WhatsApp · 917 240 397
            </a>
          </div>
        </div>

        {/* Bottom legal */}
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 mt-14 pt-6 border-t border-[#F5EDD9]/10 text-center">
          <p
            className="font-sans-bbq"
            style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.4)", letterSpacing: "0.32em", textTransform: "uppercase" }}
          >
            © 2025 BBQ Assador · Costa da Caparica · Todos os direitos reservados
          </p>
        </div>
      </footer>

      <WhatsAppFloat theme="bbq" label="Reservar" />
    </main>
  );
}

const InfoRowDark = ({ icon: Icon, title, children }) => (
  <div>
    <div className="flex items-center gap-3 mb-3">
      <Icon className="h-4 w-4 text-[#E85A1F]" strokeWidth={1.5} />
      <span className="text-[10px] tracking-[0.4em] uppercase text-[#9A8F7D] font-sans-bbq">
        {title}
      </span>
    </div>
    <div className="font-sans-bbq text-sm sm:text-base text-[#F5EDD9] leading-relaxed">
      {children}
    </div>
  </div>
);

/* ============ Minimalist hand-drawn glyphs (no emojis, no stock icons) ============ */
const GrillGlyph = ({ className = "" }) => (
  <svg viewBox="0 0 32 32" fill="none" className={className} aria-hidden>
    {/* Flame */}
    <path
      d="M16 4c-1 3 1.5 4 1.5 7 0 1.5-1 2.5-2 2.5-1.5 0-2.5-1-2.5-2.8 0-1 .3-1.7.3-1.7-1.5 1.2-3 3-3 5.5 0 3.3 2.7 5.5 5.7 5.5s5.7-2.2 5.7-5.5c0-4-3-7-5.7-10.5z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    {/* Grill bars */}
    <line x1="6" y1="25" x2="26" y2="25" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="8" y1="28" x2="24" y2="28" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const PlateGlyph = ({ className = "" }) => (
  <svg viewBox="0 0 32 32" fill="none" className={className} aria-hidden>
    <ellipse cx="16" cy="16" rx="12" ry="3.5" stroke="currentColor" strokeWidth="1.2" />
    <ellipse cx="16" cy="16" rx="8" ry="2.2" stroke="currentColor" strokeWidth="1" opacity="0.6" />
    {/* Steam */}
    <path d="M12 9c-.5 1 .5 2 0 3" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.7" />
    <path d="M16 7.5c-.5 1.2 .5 2.3 0 3.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.7" />
    <path d="M20 9c-.5 1 .5 2 0 3" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.7" />
  </svg>
);

const HearthGlyph = ({ className = "" }) => (
  <svg viewBox="0 0 32 32" fill="none" className={className} aria-hidden>
    {/* Arch / shelter */}
    <path
      d="M5 26V14a11 11 0 0 1 22 0v12"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    {/* Hearth flame inside */}
    <path
      d="M16 22c-1.5-1-2-2-2-3.2 0-1.4 1-2.3 1.3-3.3.5 1 1 1.6 1.7 2.3.7-.7 1-1.5 1-2.3 1.5 1.5 2.5 3 2.5 4.5 0 1.5-1 2.7-2.5 3-1 .2-1.5.2-2 0z"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinejoin="round"
    />
    {/* Floor */}
    <line x1="3" y1="26" x2="29" y2="26" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);
